import {
  CalculateAmountParams,
  CalculateAmountResult,
  CalculateRemoveAmountParams,
  CalculateRemoveAmountResult, CoinMetadata,
  DepositParams,
  InputType,
  PaginationArgs,
  SdkConfig,
  SuiStakeProtocol,
  UserVaultInfo,
  UserVaultInfoResponse,
  Vault,
  VaultBalance,
  VaultHoldersResponse,
  VaultWithInfo,
  WithdrawBothParams,
  WithdrawOneSideParams,
  YtHoldResponse
} from "../types";
import { getFullnodeUrl, SuiClient } from "@mysten/sui/client";
import { handleMessageError, VaultsErrorCode } from "../errors";
import {
  buildCoinWithBalance,
  buildVaultBalance,
  calculateDepositRatio,
  getCoinAmountFromLiquidity,
  getShareLiquidityByAmount,
} from "../utils/utils";
import BN from "bn.js";
import Decimal from "../utils/decimal";
import { d, getDefaultSqrtPriceLimit, TickMath } from "../utils/tickMath";
import { coinWithBalance, Transaction, TransactionObjectArgument } from "@mysten/sui/transactions";
import {
  CLOCK_ADDRESS, IS_MMT_ORACLE_PRICE_SUI_PAIR, MMT_CLMM_PACKAGE_ID,
  MMT_CLMM_VERSION_ID,
  MMT_ORACLE_ID,
  MMT_ORACLE_PACKAGE_ID, NEMO_API_URL,
  PRICE_ADAPTER_PACKAGE_ID, PYTH_ORACLE_MAP, PYTH_STATE_ID, REGISTRY_ID, SET_PRICE_CAP_REGISTRY,
  VAULT_PACKAGE_ID,
  VAULT_VERSION_ID,
  VAULTS_WITHDRAW_MODULE,
  VaultsDepositModule,
} from "../constant";
import { batchGetObjects } from "../utils/objects";
import { MmtSDK } from "@mmt-finance/clmm-sdk";
import { bcs } from "@mysten/sui/bcs";
import { buildVault, buildVaultByPoolMap, getLpAmountByLiquidity } from "../utils/vault";
import { findSuiStakeProtocol, getExchangeRateForStake, requestStakeCoin } from "../utils/stake/stake";
import { estLiquidityAmountFromFtAmount, estLiquidityAndCoinAmountFromOneAmounts } from "../utils/est";
import { CacheUtil } from "../utils/cache/cache";
import { ExtendedPoolWithApr, TokenSchema } from "@mmt-finance/clmm-sdk/dist/types";
import {
  CACHE_KEY,
  Cacheable,
} from "../utils/cache/decorator";

export class Vaults {
  protected _config: SdkConfig
  protected _cache: CacheUtil
  protected _default_sender_address: string

  constructor(config: SdkConfig) {
    this._config = config
    this._cache = CacheUtil.getInstance();
    this._default_sender_address = '0xf55cc609b13e87470d3da78d39ad6f84458a8059eb06aa66f94103d775e8a663'
  }

  getConfig(): SdkConfig {
    return this._config
  }

  static createSDK(options: { client?: any, fullNodeUrl?: string, headers?: HeadersInit }): Vaults {
    let suiClient;
    let mmtClmmSDK;

    if (options.client) {
      suiClient = options.client;
      mmtClmmSDK = MmtSDK.NEW({network: "mainnet", client: options.client})
      return new Vaults({ suiClient: suiClient, mmtClmmSDK: mmtClmmSDK });
    }
    if (!options.fullNodeUrl) {
      suiClient = new SuiClient({ url: getFullnodeUrl('mainnet') });
      mmtClmmSDK = MmtSDK.NEW({ network: "mainnet" });
    } else {
      suiClient = new SuiClient({ url: options.fullNodeUrl });
      mmtClmmSDK = MmtSDK.NEW({
        network: "mainnet", suiClientUrl: options.fullNodeUrl, customHeaders: options.headers });
    }
    return new Vaults({ suiClient: suiClient, mmtClmmSDK: mmtClmmSDK });
  }

  public async getOwnerVaultBalance(wallet_address: any, vault_id: string): Promise<VaultBalance | null> {
    //get vault token type
    const vault = await this.getVault(vault_id);
    if (vault === undefined) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `vault not found, vault_id: ${vault_id}`);
    }
    const [lp_token_balance, coinMetadata] = await Promise.all([
      this._config.suiClient.getBalance({
        owner: wallet_address,
        coinType: vault.lp_token_type,
      }),
      this._config.suiClient.getCoinMetadata({
        coinType: vault.lp_token_type,
      })
    ])

    if (lp_token_balance.totalBalance === '0') {
      return null;
    }
    const clmm_pool = await this._config.mmtClmmSDK.Pool.getPool(vault.clmm_pool_id)
    const wrap_data = buildVaultBalance(wallet_address, vault, lp_token_balance, coinMetadata!, clmm_pool.currentSqrtPrice)

    if (wrap_data) {
      return wrap_data;
    }
    return null;
  }

  /**
   * Get position tokenA and tokenB amounts for a specific vault with price data
   * @param vault_id - The vault ID to get position amounts for
   * @returns Object containing tokenA and tokenB amounts with their decimals and price data
   */
  public async getPositionAssets(vault_id: string): Promise<{
    amount_a: string;
    amount_b: string;
    decimal_a: number;
    decimal_b: number;
    coin_type_a: string;
    coin_type_b: string;
    price_a?: number;
    price_b?: number;
    value_usd_a?: number;
    value_usd_b?: number;
    total_value_usd?: number;
    lp_usd_price?: number;
  } | null> {
    const vault = await this.getVault(vault_id);
    if (vault === undefined) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `vault not found, vault_id: ${vault_id}`);
    }

    const clmm_pool = vault.pool;

    // Get position tick range
    const { tick_lower_index, tick_upper_index, type_x, type_y } = vault.position;

    // Use MMT SDK TickMath to get sqrt prices
    const lower_sqrt_price = TickMath.tickIndexToSqrtPriceX64(tick_lower_index);
    const upper_sqrt_price = TickMath.tickIndexToSqrtPriceX64(tick_upper_index);

    // Calculate token amounts from liquidity using MMT SDK utilities
    const amount_info = getCoinAmountFromLiquidity(
      new BN(vault.liquidity),
      new BN(clmm_pool.currentSqrtPrice),
      lower_sqrt_price,
      upper_sqrt_price,
      true
    );

    // Fetch token prices using MMT SDK
    let price_a: number | undefined;
    let price_b: number | undefined;
    let value_usd_a: number | undefined;
    let value_usd_b: number | undefined;
    let total_value_usd: number | undefined;
    let lp_usd_price: number | undefined;

    try {
      // Use MMT SDK fetchTokenApi to get token price data
      const [tokenA, tokenB, coinMetadata] = await this.getTokenSchemas(vault)

      price_a = parseFloat(tokenA.price);
      price_b = parseFloat(tokenB.price);

      // Calculate USD values if prices are available
      if (price_a !== undefined && price_b !== undefined) {
        const decimal_a = parseInt(vault.decimals_a);
        const decimal_b = parseInt(vault.decimals_b);

        // Convert token amounts to decimal format and calculate USD values
        const amount_a_decimal = parseFloat(amount_info.coin_amount_a) / Math.pow(10, decimal_a);
        const amount_b_decimal = parseFloat(amount_info.coin_amount_b) / Math.pow(10, decimal_b);

        value_usd_a = amount_a_decimal * price_a;
        value_usd_b = amount_b_decimal * price_b;
        total_value_usd = value_usd_a + value_usd_b;

        // Calculate LP token USD value
        if (total_value_usd !== undefined && vault.total_supply !== '0') {
          const lp_decimals = coinMetadata?.decimals ?? 9;
          const total_supply_decimal = parseFloat(vault.total_supply) / Math.pow(10, lp_decimals);
          lp_usd_price = total_value_usd / total_supply_decimal;
        }
      }
    } catch (error) {
      console.error(`Failed to fetch token prices for vault ${vault_id}:`, error);
      throw error;
    }

    return {
      amount_a: amount_info.coin_amount_a.toString(),
      amount_b: amount_info.coin_amount_b.toString(),
      decimal_a: parseInt(vault.decimals_a),
      decimal_b: parseInt(vault.decimals_b),
      coin_type_a: type_x,
      coin_type_b: type_y,
      price_a,
      price_b,
      value_usd_a,
      value_usd_b,
      total_value_usd,
      lp_usd_price,
    };
  }

  @Cacheable(CACHE_KEY.TOKEN_SCHEMA, 'id')
  private async getTokenSchemas(vault: Vault): Promise<[TokenSchema, TokenSchema, CoinMetadata | null]> {
    const [tokenA, tokenB, coinMetadata] = await Promise.all([
      this._config.mmtClmmSDK.Pool.getToken(vault.coin_type_a),
      this._config.mmtClmmSDK.Pool.getToken(vault.coin_type_b),
      this._config.suiClient.getCoinMetadata({
        coinType: vault.lp_token_type,
      })
    ]);
    return [tokenA, tokenB, coinMetadata];
  }

  /**
   * Get remaining deposit capacity for a specific vault
   * @param vault_id - The vault ID to get remaining capacity for
   * @returns Object containing deposit limit, current position value, and remaining capacity in USD
   */
  public async getRemainingCap(vault_id: string): Promise<{
    deposit_limit_usd: number;
    current_position_value_usd: number;
    remaining_cap_usd: number;
  } | null> {
    const vault = await this.getVault(vault_id);
    if (vault === undefined) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `vault not found, vault_id: ${vault_id}`);
    }

    // Get position assets to get the current total USD value
    const positionAssets = await this.getPositionAssets(vault_id);
    if (!positionAssets) {
      return null;
    }

    // Convert deposit limit from raw amount to USD
    // deposit_limit is typically in the base token units, we need to get the current position value
    const deposit_limit_raw = parseFloat(vault.deposit_limit);

    // If deposit limit is 0, it means unlimited
    if (deposit_limit_raw === 0) {
      return {
        deposit_limit_usd: Number.MAX_SAFE_INTEGER,
        current_position_value_usd: positionAssets.total_value_usd || 0,
        remaining_cap_usd: Number.MAX_SAFE_INTEGER,
      };
    }

    // For now, we'll use the total position value as the basis
    // This assumes the deposit_limit is in USD terms or equivalent
    const current_position_value_usd = positionAssets.total_value_usd || 0;
    const deposit_limit_usd = deposit_limit_raw / Math.pow(10, 9); // Assuming deposit_limit is already in USD terms
    const remaining_cap_usd = Math.max(0, deposit_limit_usd - current_position_value_usd);

    return {
      deposit_limit_usd,
      current_position_value_usd,
      remaining_cap_usd,
    };
  }

  public async getOwnerVaultsBalance(wallet_address: any): Promise<VaultBalance[]> {
    const data = await this.getVaultList()
    const result = []
    for (let i = 0; i < data.length; i++) {
      const vault = data[i]
      const lp_token_balance = await this._config.suiClient.getBalance({
        owner: wallet_address,
        coinType: vault.lp_token_type,
      })
      if (lp_token_balance.totalBalance === '0') {
        continue;
      }
      const coinMetadata = await this._config.suiClient.getCoinMetadata({
        coinType: vault.lp_token_type,
      })
      const clmm_pool = await this._config.mmtClmmSDK.Pool.getPool(vault.clmm_pool_id)
      const wrap_data = buildVaultBalance(wallet_address, vault, lp_token_balance, coinMetadata!, clmm_pool.currentSqrtPrice)
      if (wrap_data) {
        result.push(wrap_data)
      }
    }
    return result
  }

  public async getOwnerVaultsBalanceObj(wallet_address: any): Promise<Record<string, VaultBalance>> {

    let warpIds
    let coinMetadataList: string[];
    try {
      const response = await fetch(`${NEMO_API_URL}/market/vaultIdList`);
      if (!response.ok) {
        return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault IDs: ${response.statusText}`);
      }
      const data = await response.json(); // 如果是 JSON 响应

      warpIds = data.data.map((item: { vaultId: string }) => item.vaultId);
      coinMetadataList = data.data.map((item: { vaultId: string, coinMetadataId: string }) => item.coinMetadataId);
    } catch (error) {
      return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault IDs: ${error}`);
    }

    const vaults = await this.getVaultByIds(warpIds)

    const vaultObj: Record<string, VaultBalance> = {};

    const coinBalances = await this._config.suiClient.getAllBalances({ owner: wallet_address });
    const coinBalanceMap = Object.fromEntries(
      coinBalances.map((coinBalance) => [coinBalance.coinType, coinBalance.totalBalance])
    );

    const coinMetadatas = await this._config.suiClient.multiGetObjects({
      ids: coinMetadataList,
      options: { showType: true, showContent: true, showDisplay: true, showOwner: true },
    });
    const coinMetadataMap: Record<string, number> = {};
    for (const coinMetadata of coinMetadatas) {
      const metadata = coinMetadata.data?.content as unknown as { fields: { decimals: number } };
      const type = coinMetadata.data?.type;
      if (metadata && type) {
        const match = type.match(/<([^>]+)>/);
        if (match) {
          coinMetadataMap[match[1]] = metadata.fields.decimals;
        }
      }
    }

    for (let i = 0; i < vaults.length; i++) {
      const vault = vaults[i]
      const totalBalance = coinBalanceMap[vault.lp_token_type];
      if (totalBalance === '0') {
        continue;
      }
      const decimals = coinMetadataMap[vault.lp_token_type];
      if (!decimals) {
        return handleMessageError(VaultsErrorCode.FetchError, `Coin metadata not found for type: ${vault.lp_token_type}`);
      }
      const wrap_data = buildVaultBalance(
        wallet_address, vault, { totalBalance: totalBalance }, { decimals: decimals }, vault.pool.currentSqrtPrice)

      if (wrap_data) {
        vaultObj[wrap_data.vault_id] = wrap_data;
      }
    }
    return vaultObj
  }

  /**
 * Get list of vault holders for a specific vault with their USD values
 * @param vault_id - The vault ID to get holders for
 * @returns Vault holders response with addresses and USD values
 */
  public async getVaultHolders(vault_id: string): Promise<VaultHoldersResponse> {
    try {
      const response = await fetch(`${NEMO_API_URL}/market/vault/holderList?vaultId=${vault_id}`);
      if (!response.ok) {
        return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault holders: ${response.statusText}`);
      }
      const data = await response.json();

      if (!data.data || typeof data.data !== 'object') {
        return handleMessageError(VaultsErrorCode.FetchError, 'Invalid response format for vault holders');
      }

      return data as VaultHoldersResponse;
    } catch (error) {
      return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault holders: ${error}`);
    }
  }

  /**
 * Get detailed vault information including APY, earnings, and token details
 * @param vault_id - The vault ID to get information for
 * @returns Vault information with earnings, APY, and token details
 */
  public async getVaultInfo(vault_id: string): Promise<UserVaultInfo> {
    try {
      const params = new URLSearchParams({
        vaultId: vault_id
      });

      const response = await fetch(`${NEMO_API_URL}/market/user/vaultInfo?${params}`);
      if (!response.ok) {
        return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault info: ${response.statusText}`);
      }

      const data: UserVaultInfoResponse = await response.json();

      if (!data.data || !Array.isArray(data.data)) {
        return handleMessageError(VaultsErrorCode.FetchError, 'Invalid response format for vault info');
      }

      return data.data[0];
    } catch (error) {
      return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault info: ${error}`);
    }
  }

  /**
   * Get YT (Yield Token) hold details for a specific address
   * @param address - The wallet address to get YT hold details for
   * @returns YT hold information including market state, volume, and YT amount
   */
  public async getYtHoldDetail(address: string): Promise<YtHoldResponse> {
    try {
      const params = new URLSearchParams({
        address: address
      });

      const response = await fetch(`${NEMO_API_URL}/ytHold/detail?${params}`);
      if (!response.ok) {
        return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch YT hold detail: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.data || typeof data.data !== 'object') {
        return handleMessageError(VaultsErrorCode.FetchError, 'Invalid response format for YT hold detail');
      }

      return data as YtHoldResponse;
    } catch (error) {
      return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch YT hold detail: ${error}`);
    }
  }

  public async getOwnerVaultsBalanceByVaultId(wallet_address: any, vault_id: string): Promise<VaultBalance | undefined> {
    const vault = await this.getVault(vault_id);
    if (vault === undefined) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `vault not found, vault_id: ${vault_id}`);
    }
    const lp_token_balance = await this._config.suiClient.getBalance({
      owner: wallet_address,
      coinType: vault.lp_token_type,
    })
    if (lp_token_balance.totalBalance === '0') {
      return undefined;
    }
    const clmm_pool = await this._config.mmtClmmSDK.Pool.getPool(vault.clmm_pool_id)
    const coinMetadata = await this._config.suiClient.getCoinMetadata({
      coinType: vault.lp_token_type,
    })
    const wrap_data = buildVaultBalance(wallet_address, vault, lp_token_balance, coinMetadata!, clmm_pool.currentSqrtPrice)
    return wrap_data;
  }

  async getAllVaultObj(pagination_args: PaginationArgs = 'all'): Promise<Record<string, Vault>> {
    const poolList = await this.getVaultList();
    const vaultObj: Record<string, Vault> = {};
    for (const vault of poolList) {
      vaultObj[vault.id] = vault;
    }
    return vaultObj
  }

  async getVaultList(pagination_args: PaginationArgs = 'all'): Promise<Vault[]> {
    let warpIds
    try {
      const response = await fetch(`${NEMO_API_URL}/market/vaultIdList`);
      if (!response.ok) {
        return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault IDs: ${response.statusText}`);
      }
      const data = await response.json(); // 如果是 JSON 响应

      warpIds = data.data.map((item: { vaultId: string }) => item.vaultId);
    } catch (error) {
      return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault IDs: ${error}`);
    }

    return await this.getVaultByIds(warpIds)
  }

  private async getVaultByIds(ids: string[]): Promise<Vault[]> {
    const objectList = await batchGetObjects(this._config.suiClient, ids, {
      showType: true,
      showContent: true,
      showDisplay: true,
      showOwner: true,
    })
    const extendedPoolWithAprs = await this._config.mmtClmmSDK.Pool.getAllPools();
    const poolMap = Object.fromEntries(
      extendedPoolWithAprs.map(item => [item.poolId, item])
    ) as Record<string, ExtendedPoolWithApr>;

    const vaults: Vault[] = []
    for (const item of objectList) {
      try {
        const vault = await buildVaultByPoolMap(poolMap, this._config.suiClient, item)
        if (vault) {
          vaults.push(vault)
        }
      } catch (error) {
        return handleMessageError(VaultsErrorCode.BuildError, `Failed to build vault: ${JSON.stringify(item)}, ${error}`)
      }
    }
    return vaults
  }

  @Cacheable(CACHE_KEY.VAULT)
  async getVault(id: string, force_refresh = false): Promise<VaultWithInfo | undefined> {
    try {
      const item: any = await this._config.suiClient.getObject({
        id: id,
        options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
      })

      const [vault, vault_info] = await Promise.all(
        [buildVault(this._config.mmtClmmSDK, this._config.suiClient, item), this.getVaultInfo(id)])
      if (vault) {
        return {...vault, vault_info}
      } else {
        handleMessageError(VaultsErrorCode.ObjectNotFound, `vault not found, vault info:${item}`);
      }
    } catch (error) {
      handleMessageError(VaultsErrorCode.FetchError, String(error));
    }
  }

  private async getVaultAndPool(vaultId: string) {
    // Get vault information
    const vault = await this.getVault(vaultId)

    if (vault === undefined) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, 'vault not found')
    }
    return {
      vault,
      pool: vault.pool,
    }
  }

  /**
   * Check if a vault is currently rebalancing
   * @param vault - The vault to check
   * @returns true if the vault is rebalancing, false otherwise
   */
  private isVaultRebalancing(vault: Vault): boolean {
    const pool = vault.pool;
    const current_sqrt_price = pool.currentSqrtPrice;
    const upperTriggerPrice = vault.upper_trigger_price;
    const lowerTriggerPrice = vault.lower_trigger_price;
    
    // Check if price is outside trigger range
    const conditionPrice = Decimal(current_sqrt_price).lte(Decimal(lowerTriggerPrice))
        || Decimal(current_sqrt_price).gte(Decimal(upperTriggerPrice));

    // Check if current tick is outside position range
    const currTick = TickMath.sqrtPriceX64ToTickIndex(new BN(current_sqrt_price));
    const tickLowerIndex = vault.position.tick_lower_index;
    const tickUpperIndex = vault.position.tick_upper_index;
    const conditionTick = currTick <= tickLowerIndex || currTick >= tickUpperIndex;

    return conditionPrice || conditionTick;
  }

  public async calculateDepositAmount(
    params: CalculateAmountParams,
    should_request_stake = true,
    adjust_best_amount = false
  ): Promise<CalculateAmountResult> {
    if (params.side === InputType.Both) {
      return await this.calculateAmountFromBoth(params, true)
    }
    return await this.calculateDepositAmountFromOneSide(params, should_request_stake, adjust_best_amount)
  }

  private async calculateAmountFromBoth(params: CalculateAmountParams, round_up: boolean): Promise<CalculateAmountResult> {
    const { vault_id, input_amount, is_amount_a, slippage } = params
    // Get vault information
    const { vault, pool } = await this.getVaultAndPool(vault_id)

    // Extract position details
    // const {position} = vault
    const position = vault.position
    const lower_tick = position.tick_lower_index
    const upper_tick = position.tick_upper_index

    const liquidity_input = estLiquidityAndCoinAmountFromOneAmounts(
      lower_tick,
      upper_tick,
      new BN(input_amount),
      is_amount_a,
      round_up,
      slippage,
      new BN(pool.currentSqrtPrice)
    )

    const ft_amount = getLpAmountByLiquidity(vault, liquidity_input.liquidity_amount.toString())

    return {
      request_id: params.input_amount,
      side: InputType.Both,
      amount_a: liquidity_input.coin_amount_a.toString(),
      amount_b: liquidity_input.coin_amount_b.toString(),
      amount_limit_a: liquidity_input.coin_amount_limit_a.toString(),
      amount_limit_b: liquidity_input.coin_amount_limit_b.toString(),
      original_input_amount: params.input_amount,
      ft_amount,
      is_amount_a,
    }
  }

  private async calculateDepositAmountFromOneSide(
    params: CalculateAmountParams,
    should_request_stake: boolean,
    adjust_best_amount = false,
    use_route = true,
    max_loop_limit = 5,
    max_remain_rate = 0.02
  ): Promise<CalculateAmountResult> {
    try {
      const { vault_id, input_amount, is_amount_a: fix_input_amount_a, slippage } = params
      // Get vault information
      const { vault, pool } = await this.getVaultAndPool(vault_id)
      const { position } = vault
      const lowerTick = position.tick_lower_index
      const upperTick = position.tick_upper_index

      const { ratio_a, ratio_b } = calculateDepositRatio(lowerTick, upperTick, new BN(pool.currentSqrtPrice))

      const fix_amount = d(input_amount).mul(fix_input_amount_a ? ratio_a : ratio_b)
      const swap_amount = d(input_amount).sub(fix_amount)
      const a2b = fix_input_amount_a
      if (swap_amount.toFixed(0) === '0') {
        return await this.calculateAmountFromBoth(params, true)
      }

      let is_amount_a
      let swap_data
      let pares_swap_data
      let after_sqrt_price
      let swap_in_amount
      let swap_out_amount
      let swap_out_amount_limit
      const suiStakeProtocol = findSuiStakeProtocol(vault.coin_type_a, vault.coin_type_b, fix_input_amount_a)
      if (suiStakeProtocol !== SuiStakeProtocol.Mmt) {
        swap_data = await this.calculateStakeDepositFixSui({
          input_sui_amount: d(params.input_amount),
          swap_sui_amount: swap_amount,
          lower_tick: lowerTick,
          upper_tick: upperTick,
          cur_sqrt_price: pool.currentSqrtPrice.toString(),
          remain_rate: 0.01,
          fix_coin_a: params.is_amount_a,
          rebalance_count: 0,
          should_request_stake: should_request_stake,
          left_sui_amount: a2b ? new Decimal(swap_amount.toFixed(0)) : new Decimal(0),
          right_sui_amount: a2b ? new Decimal(params.input_amount) : new Decimal(swap_amount.toFixed(0)),
          slippage,
          stake_protocol: suiStakeProtocol,
        })
        after_sqrt_price = pool.currentSqrtPrice.toString()
        is_amount_a = swap_data.fix_amount_a
        swap_in_amount = swap_data.swap_in_amount
        swap_out_amount = swap_data.swap_out_amount
        swap_out_amount_limit = swap_data.swap_out_amount_limit
      } else {
        const tx = new Transaction();
        tx.moveCall({
          target: `${VAULT_PACKAGE_ID}::vault::get_optimal_swap_amount_for_single_sided_liquidity`,
          arguments: [
            tx.object(vault_id),
            tx.object(vault.clmm_pool_id),
            tx.pure.u64(input_amount),
            tx.pure.bool(params.is_amount_a),
            tx.pure.u64(20),
          ],
          typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
        })
        let result = await this._config.suiClient.devInspectTransactionBlock({
          sender: this._default_sender_address,
          transactionBlock: await tx.build({
            client: this._config.suiClient,
            onlyTransactionKind: true,
          }),
        });
        if (!result?.results?.[0]?.returnValues?.[0]) {
          return handleMessageError(VaultsErrorCode.FetchError, "Failed to get swap amount")
        }
        let out_amount = Decimal(bcs.U64.parse(new Uint8Array(result.results[0].returnValues[0][0])));
        swap_out_amount_limit = out_amount
        is_amount_a = params.is_amount_a
        swap_in_amount = out_amount
        // swap_out_amount = out_amount
        after_sqrt_price = pool.currentSqrtPrice.toString()
      }

      const coin_amount = is_amount_a === fix_input_amount_a ? d(input_amount).sub(swap_in_amount).toFixed(0) : swap_out_amount_limit

      const liquidity_input = estLiquidityAndCoinAmountFromOneAmounts(
        lowerTick,
        upperTick,
        new BN(coin_amount),
        is_amount_a,
        true,
        slippage,
        new BN(after_sqrt_price)
      )

      const amount_a = liquidity_input.coin_amount_a.toString()
      const amount_b = liquidity_input.coin_amount_b.toString()

      const lp_amount = getLpAmountByLiquidity(vault, liquidity_input.liquidity_amount.toString())
      return {
        request_id: params.input_amount,
        side: InputType.OneSide,
        amount_a,
        amount_b,
        amount_limit_a: liquidity_input.coin_amount_limit_a.toString(),
        amount_limit_b: liquidity_input.coin_amount_limit_b.toString(),
        ft_amount: lp_amount,
        original_input_amount: params.input_amount,
        is_amount_a: is_amount_a,
        swap_result: {
          swap_in_amount: swap_in_amount.toString(),
          swap_out_amount: amount_b.toString(),
          a2b: fix_input_amount_a,
          sui_stake_protocol: suiStakeProtocol,
          // route_obj: swap_data.route_obj,
          is_exceed: true,
          after_sqrt_price: after_sqrt_price,
        },
      }
    } catch (error) {
      if (use_route && (String(error) === 'Error: route unavailable' || String(error) === 'Error: router timeout')) {
        return await this.calculateDepositAmountFromOneSide(params, should_request_stake, false)
      }
      throw error
    }
  }

  /**
   * @param params
   */
  async calculateStakeDepositFixSui(params: {
    input_sui_amount: Decimal
    swap_sui_amount: Decimal
    left_sui_amount: Decimal
    right_sui_amount: Decimal
    lower_tick: number
    upper_tick: number
    cur_sqrt_price: string
    remain_rate: number
    fix_coin_a: boolean
    rebalance_count: number
    should_request_stake: boolean
    stake_protocol: SuiStakeProtocol
    slippage: number
    exchange_rate?: string
  }): Promise<any | null> {
    const remain_sui_limit = params.input_sui_amount.mul(params.remain_rate)
    const remain_sui = params.input_sui_amount.sub(params.swap_sui_amount)
    let exchange_rate
    if (params.should_request_stake) {
      exchange_rate = await getExchangeRateForStake(
        this,
        params.stake_protocol,
        this._default_sender_address,
        params.should_request_stake,
        Number(params.swap_sui_amount.toFixed(0))
      )
    } else {
      exchange_rate = params.exchange_rate
        ? params.exchange_rate
        : await getExchangeRateForStake(
          this,
          params.stake_protocol,
          this._default_sender_address,
          params.should_request_stake,
          Number(params.swap_sui_amount.toFixed(0))
        )
    }
    const hasui_amount = params.swap_sui_amount.div(exchange_rate).toFixed(0, Decimal.ROUND_DOWN)

    const liquidity_input = estLiquidityAndCoinAmountFromOneAmounts(
      params.lower_tick,
      params.upper_tick,
      new BN(hasui_amount.toString()),
      !params.fix_coin_a,
      true,
      0,
      new BN(params.cur_sqrt_price)
    )
    const use_sui_amount = params.fix_coin_a ? liquidity_input.coin_amount_a.toString() : liquidity_input.coin_amount_b.toString()
    const act_remain_sui = d(remain_sui).sub(use_sui_amount)

    if (
      (act_remain_sui.greaterThanOrEqualTo(0) && act_remain_sui.lessThanOrEqualTo(remain_sui_limit)) ||
      params.rebalance_count > 12 ||
      params.left_sui_amount.greaterThanOrEqualTo(params.right_sui_amount)
    ) {
      return {
        swap_in_amount: params.swap_sui_amount.toFixed(0),
        swap_out_amount: hasui_amount,
        swap_out_amount_limit: d(hasui_amount)
          .mul(1 - params.slippage)
          .toFixed(0),
        after_sqrt_price: params.cur_sqrt_price,
        fix_amount_a: !params.fix_coin_a,
        is_exceed: true,
        request_id: '',
        stake_protocol: params.stake_protocol,
      }
    }
    if (act_remain_sui.lessThan(0)) {
      return await this.calculateStakeDepositFixSui({
        ...params,
        right_sui_amount: params.swap_sui_amount,
        swap_sui_amount: params.swap_sui_amount.add(params.left_sui_amount).div(2),
        exchange_rate,
        rebalance_count: params.rebalance_count + 1,
      })
    }

    if (act_remain_sui.greaterThan(remain_sui_limit)) {
      return await this.calculateStakeDepositFixSui({
        ...params,
        left_sui_amount: params.swap_sui_amount,
        swap_sui_amount: params.swap_sui_amount.add(params.right_sui_amount).div(2),
        exchange_rate,
        rebalance_count: params.rebalance_count + 1,
      })
    }

    return null
  }


  async deposit(params: DepositParams, sender_address: string, tx: Transaction): Promise<TransactionObjectArgument | undefined> {
    const { vault_id, slippage, coin_object_a, coin_object_b, return_coin, deposit_result } = params
    const { swap_result, amount_a, amount_b, is_amount_a, partner, side, original_input_amount } = deposit_result
    const { vault, pool } = await this.getVaultAndPool(vault_id)

    let primaryCoinAInputs
    let primaryCoinBInputs
    let in_coin
    if (side === InputType.OneSide && swap_result) {
      in_coin = (swap_result.a2b ? coin_object_a : coin_object_b) ||
        tx.add(coinWithBalance({
          balance: BigInt(original_input_amount),
          type: swap_result.a2b ? pool.tokenXType : pool.tokenYType
        }))

      const spitAmounts = [
        swap_result.swap_in_amount,
        d(original_input_amount).sub(d(swap_result.swap_in_amount)).toFixed(0, Decimal.ROUND_DOWN),
      ]
      const [swap_in_coin, amount_coin] = tx.splitCoins(in_coin, spitAmounts)

      const { swap_out_coin } = await this.handleDepositSwap(
        {
          coin_type_a: pool.tokenXType,
          coin_type_b: pool.tokenYType,
          slippage,
          clmm_pool_address: pool.poolId,
          partner,
          swap_in_amount: swap_result.swap_in_amount,
          swap_in_coin,
          a2b: swap_result.a2b,
          sui_stake_protocol: swap_result.sui_stake_protocol,
          route_obj: swap_result.route_obj,
        },
        sender_address,
        tx
      )
      if (swap_result.a2b) {
        primaryCoinAInputs = amount_coin
        primaryCoinBInputs = swap_out_coin
      } else {
        primaryCoinAInputs = swap_out_coin
        primaryCoinBInputs = amount_coin
      }
    }

    let amount_a_limit = d(amount_a).mul(d(1).add(slippage)).toFixed(0, Decimal.ROUND_DOWN).toString()
    let amount_b_limit = d(amount_b).mul(d(1).add(slippage)).toFixed(0, Decimal.ROUND_DOWN).toString()
    let fix_amount = is_amount_a ? amount_a : amount_b

    if (side === InputType.OneSide && swap_result) {
      amount_a_limit = '18446744073709551615'
      amount_b_limit = '18446744073709551615'
      if (swap_result.a2b) {
        fix_amount = d(fix_amount).mul(d(1).sub(0.001)).toFixed(0, Decimal.ROUND_DOWN).toString()
        primaryCoinAInputs = primaryCoinAInputs || buildCoinWithBalance(
          BigInt(is_amount_a ? amount_a : deposit_result.amount_limit_a), pool.tokenXType, tx)
      } else {
        fix_amount = d(fix_amount).mul(d(1).sub(0.001)).toFixed(0, Decimal.ROUND_DOWN).toString()
        primaryCoinBInputs = primaryCoinBInputs || buildCoinWithBalance(
          BigInt(is_amount_a ? deposit_result.amount_limit_b : deposit_result.amount_b), pool.tokenYType, tx)
      }
    } else {
      primaryCoinAInputs =
        coin_object_a || buildCoinWithBalance(BigInt(is_amount_a ? amount_a : amount_a_limit), pool.tokenXType, tx)
      primaryCoinBInputs =
        coin_object_b || buildCoinWithBalance(BigInt(is_amount_a ? amount_b_limit : amount_b), pool.tokenYType, tx)
    }

    if (primaryCoinAInputs === undefined || primaryCoinBInputs === undefined) {
      return handleMessageError(VaultsErrorCode.InsufficientCoins, "Insufficient coins for deposit")
    }

    const lpCoin = await this.depositInternal(
      {
        coin_type_a: pool.tokenXType,
        coin_type_b: pool.tokenYType,
        lp_token_type: vault.lp_token_type,
        config_type: vault.config_type,
        clmm_pool_id: pool.poolId,
        primary_coin_a_inputs: primaryCoinAInputs,
        primary_coin_b_inputs: primaryCoinBInputs,
        vault_id,
        slippage: params.slippage,
        amount_a: is_amount_a ? fix_amount : amount_a_limit,
        amount_b: is_amount_a ? amount_b_limit : fix_amount,
        is_amount_a: is_amount_a,
        return_coin,
      },
      tx
    )

    if (swap_result && in_coin) {
      tx.transferObjects([in_coin], tx.pure.address(sender_address))
    }

    if (return_coin) {
      return lpCoin
    }

    return undefined
  }

  private async depositInternal(
    params: {
      vault_id: string
      coin_type_a: string
      coin_type_b: string
      amount_a: string
      amount_b: string
      slippage: number
      is_amount_a: boolean
      lp_token_type: string
      config_type: string
      clmm_pool_id: string
      primary_coin_a_inputs: TransactionObjectArgument
      primary_coin_b_inputs: TransactionObjectArgument
      return_coin?: boolean
    },
    tx: Transaction
  ) {
    let { primary_coin_a_inputs, primary_coin_b_inputs } = params

    // if (primary_coin_a_inputs === undefined || primary_coin_b_inputs === undefined) {
    //   const all_coin_asset = await getOwnerCoinAssets(this, this.getSenderAddress())
    //   primary_coin_a_inputs = PositionUtils.buildAddLiquidityFixTokenCoinInput(
    //     tx,
    //     !params.fix_amount_a,
    //     params.amount_a,
    //     params.slippage,
    //     params.coin_type_a,
    //     all_coin_asset,
    //     false
    //   )?.target_coin
    //
    //   primary_coin_b_inputs = PositionUtils.buildAddLiquidityFixTokenCoinInput(
    //     tx,
    //     params.fix_amount_a,
    //     params.amount_b,
    //     params.slippage,
    //     params.coin_type_b,
    //     all_coin_asset,
    //     false
    //   )?.target_coin
    // }

    const priceReceiptA = tx.moveCall({
      target: `${MMT_ORACLE_PACKAGE_ID}::oracle::get_price_receipt`,
      typeArguments: [params.coin_type_a],
      arguments: [tx.object(MMT_ORACLE_ID)],
    })
    if (!IS_MMT_ORACLE_PRICE_SUI_PAIR.includes(params.coin_type_a)) {
      tx.moveCall({
        target: `${PRICE_ADAPTER_PACKAGE_ID}::price_source::set_k_oracle_price`,
        typeArguments: [params.coin_type_a],
        arguments: [
          priceReceiptA,
          tx.object(MMT_ORACLE_ID),
          tx.object(REGISTRY_ID),
          tx.pure.bool(true),
          tx.object(PYTH_STATE_ID),
          tx.object(PYTH_ORACLE_MAP[params.coin_type_a].priceInfoObjectId),
          tx.object(SET_PRICE_CAP_REGISTRY),
          tx.object(CLOCK_ADDRESS),
        ],
      });
    } else {
      tx.moveCall({
        target: `${PRICE_ADAPTER_PACKAGE_ID}::price_source::set_mmt_oracle`,
        typeArguments: [params.coin_type_a, params.coin_type_b],
        arguments: [
          priceReceiptA,
          tx.object(params.clmm_pool_id),
          tx.object(MMT_ORACLE_ID),
          tx.object(PYTH_STATE_ID),
          tx.object(REGISTRY_ID),
          tx.object(PYTH_ORACLE_MAP[params.coin_type_b].priceInfoObjectId),
          tx.object(SET_PRICE_CAP_REGISTRY),
          tx.object(CLOCK_ADDRESS),
          tx.pure.bool(true),
        ],
      })
    }
    tx.moveCall({
      target: `${MMT_ORACLE_PACKAGE_ID}::oracle::update_price`,
      typeArguments: [params.coin_type_a],
      arguments: [priceReceiptA, tx.object(MMT_ORACLE_ID)],
    })

    const priceReceiptB = tx.moveCall({
      target: `${MMT_ORACLE_PACKAGE_ID}::oracle::get_price_receipt`,
      typeArguments: [params.coin_type_b],
      arguments: [tx.object(MMT_ORACLE_ID)],
    })
    tx.moveCall({
      target: `${PRICE_ADAPTER_PACKAGE_ID}::price_source::set_k_oracle_price`,
      typeArguments: [params.coin_type_b],
      arguments: [
        priceReceiptB,
        tx.object(MMT_ORACLE_ID),
        tx.object(REGISTRY_ID),
        tx.pure.bool(true),
        tx.object(PYTH_STATE_ID),
        tx.object(PYTH_ORACLE_MAP[params.coin_type_b].priceInfoObjectId),
        tx.object(SET_PRICE_CAP_REGISTRY),
        tx.object(CLOCK_ADDRESS),
      ],
    })
    tx.moveCall({
      target: `${MMT_ORACLE_PACKAGE_ID}::oracle::update_price`,
      typeArguments: [params.coin_type_b],
      arguments: [priceReceiptB, tx.object(MMT_ORACLE_ID)],
    })

    const args = [
      tx.object(params.vault_id),
      tx.object(params.clmm_pool_id),
      primary_coin_a_inputs,
      primary_coin_b_inputs,
      tx.pure.u64(0),
      tx.pure.u64(0),
      tx.object(MMT_ORACLE_ID),
      tx.object(CLOCK_ADDRESS),
      tx.object(VAULT_VERSION_ID),
      tx.object(MMT_CLMM_VERSION_ID),
    ]

    const typeArguments = [params.coin_type_a, params.coin_type_b, params.lp_token_type, params.config_type]

    if (params.return_coin) {
      return tx.moveCall({
        target: `${VAULT_PACKAGE_ID}::${VaultsDepositModule}::deposit_non_entry`,
        typeArguments,
        arguments: args,
      })
    }
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::${VaultsDepositModule}::deposit_entry`,
      typeArguments,
      arguments: args,
    })

    return undefined
  }

  private async handleDepositSwap(
    params: {
      partner: any
      coin_type_a: string
      coin_type_b: string
      slippage: number
      clmm_pool_address: string
      swap_in_amount: string
      swap_in_coin: TransactionObjectArgument
      a2b: boolean
      sui_stake_protocol: SuiStakeProtocol
      route_obj?: any
    },
    sender_address: string,
    tx: Transaction
  ) {
    const {
      partner,
      coin_type_a,
      coin_type_b,
      slippage,
      clmm_pool_address,
      swap_in_amount,
      a2b,
      sui_stake_protocol,
      route_obj,
      swap_in_coin,
    } = params
    const swap_coin_input_from = swap_in_coin

    if (sui_stake_protocol !== SuiStakeProtocol.Mmt) {
      const ha_sui_coin = requestStakeCoin(sui_stake_protocol, tx, swap_coin_input_from)!
      return {
        swap_out_coin: ha_sui_coin,
      }
    }
    // if (route_obj) {
    //   const routerParamsV2: BuildRouterSwapParamsV2 = {
    //     routers: route_obj,
    //     inputCoin: swap_coin_input_from,
    //     slippage,
    //     txb: tx,
    //     partner,
    //   }
    //
    //   let client: AggregatorClient = this._sdk.AggregatorClient
    //
    //   const toCoin = await client.fixableRouterSwap(routerParamsV2)
    //
    //   return {
    //     swap_out_coin: toCoin,
    //   }
    // }
    // const swap_coin_input_to = buildCoinWithBalance(BigInt(swap_in_amount), a2b ? coin_type_b : coin_type_a, tx)
    const sqrt_price_limit = getDefaultSqrtPriceLimit(a2b)
    const coinABs = this._config.mmtClmmSDK.Pool.swap(
      tx,
      {
        objectId: clmm_pool_address,
        tokenXType: coin_type_a,
        tokenYType: coin_type_b,
      },
      BigInt(swap_in_amount),
      swap_coin_input_from,
      a2b,
      undefined,
      BigInt(sqrt_price_limit),
      true)

    const swapOutCoin = coinABs

    tx.transferObjects([swap_coin_input_from], tx.pure.address(sender_address))

    return {
      swap_out_coin: swapOutCoin,
    }
  }

  async withdraw(
    params: WithdrawBothParams | WithdrawOneSideParams,
    sender_address: string,
    tx: Transaction
  ): Promise<{ return_coin_a?: TransactionObjectArgument; return_coin_b?: TransactionObjectArgument }> {
    const isOneSide = 'is_ft_input' in params
    const { vault, pool } = await this.getVaultAndPool(params.vault_id)
    const isReturnCoin = params.return_coin ?? false

    // Check if vault is rebalancing and set slippage to 0 if so
    const isRebalancing = this.isVaultRebalancing(vault)
    const effectiveSlippage = isRebalancing ? 0 : params.slippage

    let burn_ft_amount
    let min_amount_a
    let min_amount_b
    let oneSideRes: CalculateRemoveAmountResult | undefined
    if (isOneSide) {
      oneSideRes = await this.calculateWithdrawAmount({
        ...params,
        slippage: effectiveSlippage,
        side: InputType.OneSide,
      })
      min_amount_a = oneSideRes.amount_limit_a
      min_amount_b = oneSideRes.amount_limit_b
      burn_ft_amount = params.is_ft_input ? params.input_amount : oneSideRes.burn_ft_amount
    } else {
      const { vault_id, ft_amount } = params
      burn_ft_amount = ft_amount
      const vault = await this.getVault(vault_id, true)
      if (vault === undefined) {
        return handleMessageError(VaultsErrorCode.ObjectNotFound, 'Vault not found')
      }
      const res = await estLiquidityAmountFromFtAmount({
        input_ft_amount: ft_amount,
        slippage: effectiveSlippage,
        vault: vault
      })

      min_amount_a = res.amount_limit_a
      min_amount_b = res.amount_limit_b
    }

    const { receive_coin_a, receive_coin_b } = await this.withdrawInternal(
      {
        coin_type_a: pool.tokenXType,
        coin_type_b: pool.tokenYType,
        lp_token_type: vault.lp_token_type,
        config_type: vault.config_type,
        clmm_pool: pool.poolId,
        min_amount_a,
        min_amount_b,
        vault_id: params.vault_id,
        ft_amount: burn_ft_amount,
        primary_coin_inputs: 'primary_coin_inputs' in params ? params.primary_coin_inputs : undefined,
      },
      tx
    )

    if (isOneSide && oneSideRes) {
      const { a2b, swap_in_amount, route_obj } = oneSideRes.swap_result!
      if (route_obj) {
        // const swap_coin_input_from: BuildCoinResult = {
        //   target_coin: a2b ? receive_coin_a : receive_coin_b,
        //   remain_coins: [],
        //   is_mint_zero_coin: false,
        //   target_coin_amount: '',
        //   selected_coins: [],
        // }
        //
        // const routerParamsV2 = {
        //   routers: route_obj,
        //   inputCoin: swap_coin_input_from.target_coin,
        //   slippage: params.slippage,
        //   txb: tx,
        //   partner: params.partner,
        // }
        // let client = this._sdk.AggregatorClient
        //
        // const to_coin = await client.fixableRouterSwap(routerParamsV2)
        // const coin_abs = a2b ? [swap_coin_input_from.target_coin, to_coin] : [to_coin, swap_coin_input_from.target_coin]
        //
        // if (a2b) {
        //   tx.mergeCoins(coin_abs[1], [receive_coin_b])
        // } else {
        //   tx.mergeCoins(coin_abs[0], [receive_coin_a])
        // }
        // if (isReturnCoin) {
        //   return {
        //     return_coin_a: a2b ? undefined : to_coin,
        //     return_coin_b: a2b ? to_coin : undefined,
        //   }
        // }
        // tx.transferObjects([to_coin], tx.pure.address(this._sdk.getSenderAddress()))
      } else {
        const sqrt_price_limit = getDefaultSqrtPriceLimit(a2b)
        const receiveAmount = tx.moveCall({
          target: "0x2::coin::value",
          arguments: [a2b ? receive_coin_a : receive_coin_b],
          typeArguments: [a2b ? pool.tokenXType : pool.tokenYType],
        });
        const coin = this._config.mmtClmmSDK.Pool.swap(
          tx,
          {
            objectId: pool.poolId,
            tokenXType: pool.tokenXType,
            tokenYType: pool.tokenYType,
          },
          receiveAmount,
          a2b ? receive_coin_a : receive_coin_b,
          a2b,
          undefined,
          BigInt(sqrt_price_limit),
          true)
        if (a2b) {
          tx.mergeCoins(receive_coin_b, [coin])
        } else {
          tx.mergeCoins(receive_coin_a, [coin])
        }
        if (isReturnCoin) {
          return {
            return_coin_a: receive_coin_a,
            return_coin_b: receive_coin_b,
          };
        }
        tx.transferObjects([receive_coin_a, receive_coin_b], tx.pure.address(sender_address))
      }
    } else if (isReturnCoin) {
      return {
        return_coin_a: receive_coin_a,
        return_coin_b: receive_coin_b,
      }
    } else {
      tx.transferObjects([receive_coin_a, receive_coin_b], tx.pure.address(sender_address))
    }

    return {}
  }


  async calculateWithdrawAmount(params: CalculateRemoveAmountParams): Promise<CalculateRemoveAmountResult> {
    // Get vault to check if rebalancing
    const vault = await this.getVault(params.vault_id)
    if (vault === undefined) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, 'Vault not found')
    }

    // Check if vault is rebalancing and set slippage to 0 if so
    const isRebalancing = this.isVaultRebalancing(vault)
    const effectiveSlippage = isRebalancing ? 0 : params.slippage

    if (params.side === InputType.Both) {
      if (params.is_ft_input) {
        const amounts = await estLiquidityAmountFromFtAmount({
          input_ft_amount: params.input_amount,
          slippage: effectiveSlippage,
          vault: vault
        });
        return {
          ...amounts,
          request_id: params.input_amount,
          burn_ft_amount: params.input_amount,
          side: params.side,
        }
      }
      const res = await this.calculateAmountFromBoth({
        vault_id: params.vault_id,
        is_amount_a: params.is_amount_a,
        input_amount: params.input_amount,
        slippage: effectiveSlippage,
        side: params.side,
      }, false)
      return {
        ...res,
        request_id: params.input_amount,
        burn_ft_amount: res.ft_amount,
        side: params.side,
      }
    }

    if (!+params.max_ft_amount) {
      return handleMessageError(VaultsErrorCode.InvalidMaxFtAmount, 'max_ft_amount input is invalid')
    }

    const max_liquidity = getShareLiquidityByAmount(vault, params.max_ft_amount)
    return await this.calculateWithdrawAmountFromOneSide(
      {
        fix_amount_a: params.is_amount_a,
        vault_id: params.vault_id,
        receive_amount: params.is_ft_input ? '0' : params.input_amount,
        slippage: effectiveSlippage,
        max_liquidity,
        remove_liquidity: params.is_ft_input ? getShareLiquidityByAmount(vault, params.input_amount) : undefined,
      },
      true
    )
  }

  private async calculateWithdrawAmountFromOneSide(
    params: {
      fix_amount_a: boolean
      vault_id: string
      receive_amount: string
      slippage: number
      remove_liquidity?: string
      max_liquidity: string
    },
    use_route: boolean,
    range?: {
      left: Decimal
      right: Decimal
      count: number
    }
  ): Promise<CalculateRemoveAmountResult> {
    try {
      const { vault_id, remove_liquidity, max_liquidity } = params
      // Get vault information
      const { vault, pool } = await this.getVaultAndPool(vault_id)
      const { position } = vault
      const lowerTick = position.tick_lower_index
      const upperTick = position.tick_upper_index

      const is_remove_all = remove_liquidity === max_liquidity

      const ratios = calculateDepositRatio(lowerTick, upperTick, new BN(pool.currentSqrtPrice))
      const fix_ratio = params.fix_amount_a ? ratios.ratio_a : ratios.ratio_b

      let fix_amount = d(params.receive_amount).mul(fix_ratio)
      let other_side_amount
      let liquidity

      // Remove by liquidity
      if (remove_liquidity) {
        const lower_sqrt_price = TickMath.tickIndexToSqrtPriceX64(lowerTick)
        const upper_sqrt_price = TickMath.tickIndexToSqrtPriceX64(upperTick)
        const remove_params = getCoinAmountFromLiquidity(
          new BN(remove_liquidity),
          new BN(pool.currentSqrtPrice),
          lower_sqrt_price,
          upper_sqrt_price,
          false
        )
        liquidity = remove_liquidity
        other_side_amount = params.fix_amount_a ? remove_params.coin_amount_b.toString() : remove_params.coin_amount_a.toString()
        fix_amount = params.fix_amount_a ? d(remove_params.coin_amount_a.toString()) : d(remove_params.coin_amount_b.toString())
      } else {
        // Fixed fix_amount_a calculation of liquidity and value in the other direction
        const remove_params = estLiquidityAndCoinAmountFromOneAmounts(
          lowerTick,
          upperTick,
          new BN(fix_amount.toFixed(0)),
          params.fix_amount_a,
          false,
          params.slippage,
          new BN(pool.currentSqrtPrice)
        )
        liquidity = remove_params.liquidity_amount.toString()
        other_side_amount = params.fix_amount_a ? remove_params.coin_amount_b.toString() : remove_params.coin_amount_a.toString()
      }
      // Swap otherSideAmount to get the expected value in the fix_amount_a direction
      const a2b = !params.fix_amount_a
      let out_amount = await this._config.mmtClmmSDK.Pool.preSwap(new Transaction(), [{
        tokenXType: pool.tokenXType,
        tokenYType: pool.tokenYType,
        poolId: pool.poolId,
        isXtoY: a2b,
      }], other_side_amount)
      // const price = TickMath.sqrtPriceX64ToPrice(new BN(pool.currentSqrtPrice), Number(vault.decimals_a), Number(vault.decimals_b));
      // let out_amount = params.fix_amount_a ?
      //   Decimal(other_side_amount).div(Decimal(10).pow(Number(vault.decimals_b))).div(price).mul(Decimal(10).pow(Number(vault.decimals_a))) :
      //   Decimal(other_side_amount).div(Decimal(10).pow(Number(vault.decimals_a))).mul(price).mul(Decimal(10).pow(Number(vault.decimals_b)));

      const rcl_amount = fix_amount.add(Decimal(out_amount.toString()))
      const expect_amount = d(params.receive_amount)
      const ramain_amount = expect_amount.sub(rcl_amount)

      if (!is_remove_all && (!params.remove_liquidity || (params.remove_liquidity && range))) {
        if (ramain_amount.abs().greaterThan(expect_amount.mul(0.01))) {
          // amount is not enough
          const amount_insufficient = rcl_amount.lessThan(expect_amount)
          let left
          let right
          if (!range) {
            left = amount_insufficient ? d(liquidity) : d(0)
            right = amount_insufficient ? d(params.max_liquidity) : d(liquidity)
          }
          // Determine the remaining amount last time and the remaining amount this time
          else if (amount_insufficient) {
            left = d(liquidity)
            right = range.right
          } else {
            left = range.left
            right = d(liquidity)
          }

          const mid_liquidity = d(left).add(right).div(2).toFixed(0)
          if (!range || (range && range.count < 15 && left.lessThan(right))) {
            const swap_result = await this.calculateWithdrawAmountFromOneSide(
              {
                ...params,
                remove_liquidity: mid_liquidity,
              },
              use_route,
              {
                left,
                right,
                count: range ? range.count + 1 : 0,
              }
            )
            return swap_result
          }
        }
      }

      const swap_in_amount = other_side_amount
      const swap_out_amount = out_amount.toString()
      // const { is_exceed } = data

      const burn_ft_amount = getLpAmountByLiquidity(vault, liquidity.toString())

      const amounts = await estLiquidityAmountFromFtAmount({
        input_ft_amount: burn_ft_amount,
        slippage: params.slippage,
        vault
      })

      const result: CalculateRemoveAmountResult = {
        side: InputType.OneSide,
        ...amounts,
        burn_ft_amount,
        request_id: params.receive_amount,
        swap_result: {
          swap_in_amount: swap_in_amount,
          swap_out_amount: swap_out_amount,
          a2b,
          is_exceed: false,
          sui_stake_protocol: SuiStakeProtocol.Mmt,
          // route_obj: {},
        },
      }

      return result
    } catch (error) {
      if (use_route && (String(error) === 'Error: route unavailable' || String(error) === 'Error: router timeout')) {
        return await this.calculateWithdrawAmountFromOneSide(params, false)
      }
      throw error
    }
  }

  private async withdrawInternal(
    params: {
      vault_id: string
      clmm_pool: string
      coin_type_a: string
      coin_type_b: string
      ft_amount: string
      min_amount_a: string
      min_amount_b: string
      lp_token_type: string
      primary_coin_inputs?: TransactionObjectArgument
      config_type: string
    },
    tx: Transaction
  ): Promise<{ receive_coin_a: TransactionObjectArgument; receive_coin_b: TransactionObjectArgument }> {
    let { primary_coin_inputs } = params
    if (primary_coin_inputs === undefined) {
      primary_coin_inputs = buildCoinWithBalance(BigInt(params.ft_amount), params.lp_token_type, tx)
    }
    if (primary_coin_inputs === undefined) {
      return handleMessageError(VaultsErrorCode.InsufficientCoins, "Insufficient coins for deposit")
    }

    const args = [
      tx.object(params.vault_id),
      tx.object(params.clmm_pool),
      primary_coin_inputs,
      tx.pure.u64(params.min_amount_a),
      tx.pure.u64(params.min_amount_b),
      tx.object(CLOCK_ADDRESS),
      tx.object(VAULT_VERSION_ID),
      tx.object(MMT_CLMM_VERSION_ID),
    ]

    const typeArguments = [params.coin_type_a, params.coin_type_b, params.lp_token_type, params.config_type]

    const [coinA, coinB] = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::${VAULTS_WITHDRAW_MODULE}::withdraw`,
      typeArguments,
      arguments: args,
    })
    return {
      receive_coin_a: coinA,
      receive_coin_b: coinB,
    }
  }

  public async getVaultMarketHolders(vaultId?: string) {
    const response = await fetch(`${NEMO_API_URL}/market/coinInfo`);
    if (!response.ok) {
      return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch coinInfo: ${response.statusText}`);
    }
    const data = await response.json(); // 如果是 JSON 响应

    let vault;
    if (vaultId) {
      vault = await this.getVault(vaultId);
      if (!vault) {
        return handleMessageError(VaultsErrorCode.ObjectNotFound, 'Vault not found');
      }
    }

    const marketList = data.data;
    const lpHolderMap: Record<string, Decimal> = {};
    for (const market of marketList) {
      if (!market.provider || market.provider !== 'Nemo') {
        continue;
      }
      if (vault && market.coinType !== vault.lp_token_type) {
        continue;
      }

      const params = new URLSearchParams({
        coinType: market.coinType,
        maturity: market.maturity,
      });

      const lpHolderListResponse = await fetch(`${NEMO_API_URL}/market/market/lpHolderList?${params}`);
      if (!lpHolderListResponse.ok) {
        return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault market holders: ${response.statusText}`);
      }

      const lpHolderList = await lpHolderListResponse.json();
      for (const holder of lpHolderList.data.holderData) {
        if (!lpHolderMap[holder.address]) {
          lpHolderMap[holder.address] = Decimal(holder.usdValue);
        } else {
          lpHolderMap[holder.address] = lpHolderMap[holder.address].add(Decimal(holder.usdValue));
        }
      }
    }

    return Object.entries(lpHolderMap).map(([address, usdValue]) => ({
      address,
      usdValue: usdValue.toNumber().toFixed(16)
    }));
  }

  public async getVaultStatus() {
    console.log(`${NEMO_API_URL}/market/vaultShowList`)
    const response = await fetch(`${NEMO_API_URL}/market/vaultShowListV2`);
    if (!response.ok) {
      return handleMessageError(VaultsErrorCode.FetchError, `Failed to fetch vault status: ${response.statusText}`);
    }

    const data = await response.json(); // 如果是 JSON 响应
    const vaultStatusList = data.data;

    const allVault = await this.getAllVaultObj();

    const res: { vaultId: string; isRebalancing: boolean; vaultName: string; disable: boolean }[] = [];
    for (const item of vaultStatusList) {
      const vault = allVault[item.vaultId];
      if (vault === undefined) {
        throw new Error(`Vault not found for vault: ${item.vaultId}`);
      }
      const pool = vault.pool;
      if (!pool) {
        throw new Error(`Pool not found for vault: ${vault.id}`);
      }
      if (!vault.config_type.includes('Uncorrelated')) {
        throw new Error(`Invalid lp token type: ${vault.id} ${vault.lp_token_type}`);
      }

      const current_sqrt_price = pool.currentSqrtPrice;
      const upperTriggerPrice = vault.upper_trigger_price;
      const lowerTriggerPrice = vault.lower_trigger_price;
      const conditionPrice = Decimal(current_sqrt_price).lte(Decimal(lowerTriggerPrice))
          || Decimal(current_sqrt_price).gte(Decimal(upperTriggerPrice));

      const currTick = TickMath.sqrtPriceX64ToTickIndex(new BN(current_sqrt_price));
      const tickLowerIndex = vault.position.tick_lower_index;
      const tickUpperIndex = vault.position.tick_upper_index;

      const conditionTick = currTick <= tickLowerIndex || currTick >= tickUpperIndex;

      let isRebalancing = false;
      if (conditionPrice || conditionTick) {
        isRebalancing = true;
      }
      res.push({
        vaultId: item.vaultId,
        isRebalancing: isRebalancing,
        vaultName: item.vaultName,
        disable: item.disable
      })
    }

    return res;
  }
}